import EyeIcon from "@/components/icons/eye";
import PencilIcon from "@/components/icons/pencil";
import { HeaderCell } from "@/components/ui/table";
import { routes } from "@/config/routes";
import { SortConfig } from "@/hooks/useTableServer";
import {
  EPushNotificationStatus,
  IPushNotification,
} from "@/utilities/types/PushNotification";
import dayjs from "dayjs";
import { ActionIcon, Tooltip } from "rizzui";
import { Link } from "react-router-dom";
import { PiBellSlash } from "react-icons/pi";
import StatusCell from "./status-cell";

type PushNotificationSortBy =
  | "title"
  | "status"
  | "scheduledAt"
  | "sentAt"
  | "createdAt";

type Columns = {
  data: IPushNotification[];
  sortConfig?: SortConfig<PushNotificationSortBy>;
  onHeaderCellClick: (value: string) => Record<string, () => void>;
  onCancelItem: (id: string) => void;
  onViewDetail: (row: IPushNotification) => void;
};

export const getColumns = ({
  sortConfig,
  onHeaderCellClick,
  onCancelItem,
  onViewDetail,
}: Columns) => [
  {
    title: <HeaderCell title="Title" />,
    dataIndex: "title",
    key: "title",
    render: (title: string, row: IPushNotification) => (
      <button
        type="button"
        onClick={() => onViewDetail(row)}
        className="text-left font-medium text-gray-900 hover:text-primary"
      >
        <div className="line-clamp-1 max-w-xs">{title}</div>
        {row.body && (
          <div className="line-clamp-1 text-xs font-normal text-gray-500">
            {row.body}
          </div>
        )}
      </button>
    ),
  },
  {
    title: <HeaderCell title="Status" align="center" />,
    dataIndex: "status",
    key: "status",
    align: "center",
    render: (status: EPushNotificationStatus) => <StatusCell status={status} />,
  },
  {
    title: (
      <HeaderCell
        title="Scheduled / Sent"
        sortable
        align="center"
        ascending={
          sortConfig?.direction === "asc" && sortConfig?.key === "scheduledAt"
        }
      />
    ),
    key: "schedule",
    align: "center",
    onHeaderCell: () => onHeaderCellClick("scheduledAt"),
    render: (_: unknown, row: IPushNotification) => {
      if (row.status === EPushNotificationStatus.SENT && row.sentAt) {
        return (
          <div className="text-xs">
            <div className="text-gray-400">Sent</div>
            <div>{dayjs(row.sentAt).format("MMM DD, YYYY HH:mm")}</div>
          </div>
        );
      }
      if (row.status === EPushNotificationStatus.SENDING) {
        return <span className="text-xs text-yellow-700">Sending…</span>;
      }
      if (row.scheduledAt) {
        return (
          <div className="text-xs">
            <div className="text-gray-400">Scheduled</div>
            <div>{dayjs(row.scheduledAt).format("MMM DD, YYYY HH:mm")}</div>
          </div>
        );
      }
      return <span className="text-xs text-gray-400">—</span>;
    },
  },
  {
    title: (
      <HeaderCell
        title="Created At"
        sortable
        align="center"
        ascending={
          sortConfig?.direction === "asc" && sortConfig?.key === "createdAt"
        }
      />
    ),
    dataIndex: "createdAt",
    key: "createdAt",
    align: "center",
    onHeaderCell: () => onHeaderCellClick("createdAt"),
    render: (date: string) => dayjs(date).format("MMM DD, YYYY HH:mm"),
  },
  {
    title: <HeaderCell title="Actions" />,
    dataIndex: "action",
    key: "action",
    width: 180,
    render: (_: string, row: IPushNotification) => {
      const canEdit =
        row.status === EPushNotificationStatus.DRAFT ||
        row.status === EPushNotificationStatus.SCHEDULED;
      const canCancel = row.status === EPushNotificationStatus.SCHEDULED;

      return (
        <div className="flex items-center justify-start gap-3 pe-4">
          <Tooltip
            size="sm"
            content="View Details"
            placement="top"
            color="invert"
          >
            <ActionIcon
              size="sm"
              variant="outline"
              onClick={() => onViewDetail(row)}
              aria-label="View details"
            >
              <EyeIcon className="h-4 w-4" />
            </ActionIcon>
          </Tooltip>

          {canEdit && (
            <Tooltip size="sm" content="Edit" placement="top" color="invert">
              <Link to={routes.pushNotification.edit(row.id)}>
                <ActionIcon
                  size="sm"
                  variant="outline"
                  as="span"
                  aria-label="Edit"
                >
                  <PencilIcon className="h-4 w-4" />
                </ActionIcon>
              </Link>
            </Tooltip>
          )}

          {canCancel && (
            <Tooltip
              size="sm"
              content="Cancel scheduled notification"
              placement="top"
              color="invert"
            >
              <ActionIcon
                size="sm"
                variant="outline"
                onClick={() => onCancelItem(row.id)}
                aria-label="Cancel"
              >
                <PiBellSlash className="h-4 w-4" />
              </ActionIcon>
            </Tooltip>
          )}
        </div>
      );
    },
  },
];
